import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';

/** 单步引导配置 */
export interface SupportGuideStep {
  /** 标题 */
  title: string;
  /** 说明文字，支持 <b>、<br> 等简单标签 */
  text: string;
  /** 底部按钮文案 */
  btn: string;
  /** 要高亮的真实页面元素；查询不到时会轮询等待（数据异步加载），超时后退化为居中提示 */
  target?: () => HTMLElement | null;
  /** 点击按钮时执行的操作（自动演示真实页面流程） */
  action?: () => void | Promise<void>;
}

/**
 * 游戏式新手引导：镂空高亮 + 气泡讲解，高亮真实页面元素并自动演示流程。
 *
 * 用法：
 *   <app-support-guide #guide [steps]="guideSteps"
 *     (finished)="onGuideFinished()"
 *     (dismissed)="onGuideDismissed()"></app-support-guide>
 *
 *   this.guide.start() 开始引导；
 *   最后一步点击按钮时发出 finished（调用方应在此调用后端接口标记“已完成”）；
 *   按 Esc 中途跳过时发出 dismissed（不视为完成）。
 */
@Component({
  selector: 'app-support-guide',
  templateUrl: './support-guide.component.html',
  styleUrls: ['./support-guide.component.scss'],
})
export class SupportGuideComponent implements OnDestroy {
  @Input() steps: SupportGuideStep[] = [];
  /** 最后一步点击“完成”时触发（应在此调用后端接口，标记教程已完成） */
  @Output() finished = new EventEmitter<void>();
  /** 按 Esc 中途跳过时触发（不标记完成） */
  @Output() dismissed = new EventEmitter<void>();

  active = false;
  boxShow = false;
  boxCentered = false;
  current = 0;
  title = '';
  text = '';
  btn = '';
  stepLabel = '';

  holeStyle: { [k: string]: string } = {};
  boxStyle: { [k: string]: string } = {};

  private busy = false;
  private timers: ReturnType<typeof setTimeout>[] = [];
  private lastBodyOverflow = '';

  /** 从头开始引导 */
  start() {
    if (!this.steps.length || this.active) {
      return;
    }
    this.active = true;
    this.current = 0;
    // 初始状态：全屏变暗（镂空元素先归位到视口中心）
    this.holeStyle = { top: '50%', left: '50%', width: '0px', height: '0px' };
    this.lockScroll();
    void this.show();
  }

  /** “下一步” / 最后一步“完成” */
  next() {
    if (!this.active || this.busy) {
      return;
    }
    this.busy = true;
    const step = this.steps[this.current];
    this.boxShow = false;
    this.later(async () => {
      this.busy = false;
      if (!this.active) {
        return;
      }
      if (step.action) {
        try {
          await step.action();
        } catch (e) {
          console.warn('[support-guide] step action error:', e);
        }
      }
      this.current += 1;
      if (this.current >= this.steps.length) {
        this.end();
        this.finished.emit();
      } else {
        void this.show();
      }
    }, 240);
  }

  /** Esc 跳过引导（不标记完成） */
  @HostListener('document:keydown.escape')
  onEscape() {
    if (!this.active) {
      return;
    }
    this.end();
    this.dismissed.emit();
  }

  ngOnDestroy(): void {
    this.end();
  }

  private async show(): Promise<void> {
    if (!this.active) {
      return;
    }
    const step = this.steps[this.current];
    this.title = step.title;
    this.text = step.text;
    this.btn = step.btn;
    this.stepLabel = `${this.current + 1} / ${this.steps.length}`;

    if (!step.target) {
      // 无目标（开场介绍）：全屏变暗 + 居中气泡
      this.centeredLayout();
      this.later(() => {
        this.boxShow = true;
      }, 30);
      return;
    }

    // 目标元素可能随异步数据才渲染出来，轮询等待（最多 6 秒）
    const target = await this.waitFor(step.target, 6000);
    if (!this.active) {
      return;
    }
    if (!target) {
      this.centeredLayout();
      this.later(() => {
        this.boxShow = true;
      }, 30);
      return;
    }

    this.boxCentered = false;
    // 先把目标瞬时滚动到视口中央，再取坐标：
    // 避免“先取坐标后平滑滚动”导致的镂空框与目标错位
    target.scrollIntoView({ block: 'center', behavior: 'auto' });
    await this.tick(30);
    if (!this.active) {
      return;
    }
    this.positionHoleAndBox(target.getBoundingClientRect());
    this.later(() => {
      this.boxShow = true;
    }, 30);
  }

  /** 无目标：全屏变暗 + 居中气泡 */
  private centeredLayout() {
    this.boxCentered = true;
    this.holeStyle = { top: '50%', left: '50%', width: '0px', height: '0px' };
    this.boxStyle = { top: '50%', left: '50%' };
  }

  /** 按目标矩形设置镂空框与气泡位置 */
  private positionHoleAndBox(rect: {
    top: number;
    left: number;
    width: number;
    height: number;
    bottom: number;
  }) {
    const pad = 12;
    this.holeStyle = {
      top: `${Math.round(rect.top - pad)}px`,
      left: `${Math.round(rect.left - pad)}px`,
      width: `${Math.round(rect.width + pad * 2)}px`,
      height: `${Math.round(rect.height + pad * 2)}px`,
    };

    // 气泡默认放在高亮区下方；下方空间不够则放到上方；左右防溢出
    const boxWidth = 310;
    let boxTop = rect.bottom + 20;
    if (boxTop + 250 > window.innerHeight) {
      boxTop = Math.max(10, rect.top - 250);
    }
    let boxLeft = Math.min(rect.left, window.innerWidth - boxWidth - 10);
    boxLeft = Math.max(10, boxLeft);
    this.boxStyle = {
      top: `${Math.round(boxTop)}px`,
      left: `${Math.round(boxLeft)}px`,
    };
  }

  private end() {
    this.active = false;
    this.busy = false;
    this.boxShow = false;
    this.releaseScroll();
    for (const t of this.timers) {
      clearTimeout(t);
    }
    this.timers = [];
  }

  private lockScroll() {
    this.lastBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // 阻止用户滚轮滚动页面（防止高亮框与目标错位）；程序滚动不受影响
    document.addEventListener('wheel', this.wheelLock, { passive: false });
  }

  private releaseScroll() {
    document.body.style.overflow = this.lastBodyOverflow;
    document.removeEventListener('wheel', this.wheelLock);
  }

  private wheelLock = (e: WheelEvent) => {
    if (this.active) {
      e.preventDefault();
    }
  };

  private tick(ms: number): Promise<void> {
    return new Promise<void>((resolve) => {
      this.timers.push(setTimeout(() => resolve(), ms));
    });
  }

  private later(fn: () => void, ms: number) {
    this.timers.push(setTimeout(fn, ms));
  }

  private async waitFor(
    fn: () => HTMLElement | null,
    timeoutMs: number,
  ): Promise<HTMLElement | null> {
    const start = Date.now();
    for (;;) {
      const el = fn();
      if (el) {
        return el;
      }
      if (Date.now() - start > timeoutMs) {
        return null;
      }
      await this.tick(120);
    }
  }
}
